import React, { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action, Dispatch } from 'redux';

import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getCompanyLogoAndName as getLogoAndName } from '../../lib/store/fundamental';
import { formatCurrency } from '../../lib/util/DataHelpers';
import { TextHelpers } from '../../lib/util/TextHelpers';

import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import Logo from '../Chart/SymbolChart/ChartHeader/Components/Logo/Logo';
import { MobileDrawer } from '..';

import styles from './ConfirmOrderPopupMobile.module.scss';

type OwnProps = {
  symbol: string;
  action: string;
  orderType: string;
  sharescount: number | string;
  marketPrice?: number | string | null;
  targetPrice?: number | string;
  orderValue: number | string;
  onCancel: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: () => void;
};

type Props = OwnProps & ReduxProps;

const ConfirmOrderPopupMobile = (props: Props) => {
  const {
    symbol,
    action,
    orderType,
    sharescount,
    marketPrice,
    targetPrice,
    orderValue,
    companyLogoAndName,
    onCancel,
    onConfirm,
    getCompanyLogoAndName,
  } = props;

  const isMarketOrder = orderType === OrderTypeEnum.Market;
  const orderTypeText = TextHelpers.getOrderTypeName(orderType as string);

  useEffect(() => {
    if (!companyLogoAndName[symbol]) {
      getCompanyLogoAndName();
    }
  }, [companyLogoAndName, getCompanyLogoAndName, symbol]);

  const logo = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.logo : undefined), [companyLogoAndName, symbol]);

  const url = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.url : undefined), [companyLogoAndName, symbol]);

  const symbolName = companyLogoAndName[symbol]?.name;

  const renderNonMarketStateTexts = () => (
    <>
      <div className={styles.textLine}>
        <div>
          {lang.commonTradeHistoryDetailsPrice(orderTypeText)}
        </div>
        <div>{formatCurrency(targetPrice)}</div>
      </div>
      <div className={styles.textLine}>
        <div>{lang.commonTradingExpiryDate()}</div>
        <div>{lang.mobileTradingTicketDateOfExpireEndOfDayShort()}</div>
      </div>
      <div className={styles.textLine}>
        <div>{lang.commonTradingExtendedHoursTrade()}</div>
        <div>{lang.commonNo()}</div>
      </div>
    </>
  );

  return (
    <MobileDrawer isOpen onClose={() => onCancel(false)} title="" showCloseButton>
      <div className={styles.container}>
        <div className={styles.logoAndNameWrapper}>
          <Logo symbol={symbol} logo={logo} url={url} />
          <div>
            <div className={styles.symbol}>
              {symbol}
            </div>
            <div className={styles.name}>
              {symbolName}
            </div>
          </div>
        </div>
        <div className={styles.detailsContainer}>
          <div className={styles.textLine}>
            <div>{lang.commonTradingOrderType()}</div>
            <div>
              {action}
              {' '}
              {orderType}
              {' '}
              Order
            </div>
          </div>
          <div className={styles.textLine}>
            <div>{lang.mobileTradingTicketNumberOfShares()}</div>
            <div>{sharescount}</div>
          </div>
          <div className={styles.textLine}>
            <div>
              {lang.mobileTradingTicketMarketPrice(orderTypeText)}
            </div>
            <div>{formatCurrency(marketPrice)}</div>
          </div>
          {!isMarketOrder && renderNonMarketStateTexts()}
          <div className={styles.borderLine} />
          <div className={styles.textLineBold}>
            <div>{lang.commonTradingOrderValue()}</div>
            <div>{formatCurrency(orderValue)}</div>
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          <Button
            biggerFontSize
            fullWidth
            onClick={onConfirm}
          >
            {lang.commonConfirmOrderButton()}
          </Button>
          <Button
            biggerFontSize
            fullWidth
            variant={ButtonTypesEnum.Secondary}
            onClick={() => { onCancel(false); }}
          >
            {lang.commonCancelButton()}
          </Button>
        </div>
      </div>
    </MobileDrawer>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps) => ({
  getCompanyLogoAndName: () => dispatch(getLogoAndName(ownProps.symbol)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ConfirmOrderPopupMobile);
